// Maps the assorted WebAuthn (browser) and Cognito (service) errors that passkey ceremonies can throw into
// clear, user-facing copy. The exact error names/messages should be re-verified once we can test against a
// fully configured pool (Phase 0) — the matching here is deliberately broad so a new variant still lands on a
// sensible message rather than a raw stack string.
export const passkeyErrorMessage = (error: unknown, context: 'signin' | 'register'): string => {
  const err = error as { name?: string; message?: string; underlyingError?: { name?: string; message?: string } }
  const combined = [err?.name, err?.message, err?.underlyingError?.name, err?.underlyingError?.message]
    .filter(Boolean)
    .join(' ')

  // User cancelled the OS prompt or it timed out — never a hard error.
  if (/NotAllowedError|AbortError|cancel|timed out|timeout/i.test(combined)) {
    return 'The passkey prompt was cancelled or timed out. You can try again.'
  }
  // Duplicate: this authenticator already holds a credential for this account.
  if (/InvalidStateError|already registered|already exists|excludeCredentials/i.test(combined)) {
    return 'This device already has a passkey for your account.'
  }
  // Per-user credential limit reached (R11).
  if (/LimitExceeded|maximum number|too many/i.test(combined)) {
    return "You've reached the maximum number of passkeys. Remove one before adding another."
  }
  // Passkeys not available on the pool yet (WebAuthn / USER_AUTH not configured).
  if (
    /WebAuthnConfigurationMissing|WebAuthnNotEnabled|not enabled|USER_AUTH|first auth factor|InvalidParameter/i.test(
      combined
    )
  ) {
    return 'Passkey sign-in isn’t available right now. Please sign in with your password.'
  }

  if (context === 'signin') {
    // Selected credential isn't recognized by this app/environment.
    if (/UserNotFound|NotAuthorized|verification failed|no credentials|Invalid credential/i.test(combined)) {
      return 'That passkey isn’t registered for this app. Sign in with your password instead.'
    }
    return 'Passkey sign-in was cancelled or failed. Try again, or sign in with your password.'
  }

  return 'Something went wrong setting up your passkey. Please try again.'
}
